import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import CONFIG from '../../config/config';
import CrispAPI, { type CrispIdentity } from '../../api/CrispAPI';
import { Settings } from '../../store/reducers/AccountSlice';
import { isValidEmail } from '../../shared/validation';
import {
  getSessionId,
  getUnreadCount,
  hideChat,
  identifyVisitor,
  isChatOpened,
  loadCrisp,
  onCrispEvent,
  openChat as openCrispChat,
  resetSession,
  setCrispLocale,
  waitForCrispToken,
  type CrispReceivedMessage,
} from '../../services/crisp';
import { isCrispPublicSurface } from '../../services/crispPublicSurface';

const LINKED_SESSION_STORAGE_KEY = 'crisp.linkedSession';

function readLinkedSession(): string | null {
  try {
    return localStorage.getItem(LINKED_SESSION_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to read linked Crisp session from storage', { error });
    return null;
  }
}

function writeLinkedSession(value: string): void {
  try {
    localStorage.setItem(LINKED_SESSION_STORAGE_KEY, value);
  } catch (error) {
    console.error('Failed to persist linked Crisp session to storage', { error });
  }
}

type CrispChatContextValue = {
  openChat: () => void;
  unreadCount: number;
  isReady: boolean;
};

const CrispChatContext = createContext<CrispChatContextValue | null>(null);

export function CrispProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const location = useLocation();
  const settings: Settings = useSelector((state: any) => state.settings.value);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const [crispIdentity, setCrispIdentity] = useState<CrispIdentity | null | undefined>(undefined);
  const wasLoggedIn = useRef(false);
  const linkContext = useRef<{ locale?: string; plan?: string }>({});
  linkContext.current = { locale: i18n.language, plan: settings.pricingPlan };

  useEffect(() => {
    if (isCrispPublicSurface(location.pathname)) {
      hideChat();
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!settings.isLoggedIn) {
      setIsReady(false);
      setUnreadCount(0);
      setCrispIdentity(undefined);
      return;
    }

    let cancelled = false;
    waitForCrispToken();
    hideChat();
    setCrispLocale(i18n.language);

    CrispAPI.getIdentity()
      .then((response) => response.data)
      .catch((error) => {
        console.error('Failed to load Crisp identity', { tenantId: settings.tenantId, error });
        return null;
      })
      .then((identity) => {
        if (cancelled) return;
        setCrispIdentity(identity);
        loadCrisp(CONFIG.CRISP_WEBSITE_ID, identity?.tokenId);
      });

    const unsubLoaded = onCrispEvent('session:loaded', () => {
      setIsReady(true);
      linkSession();
      const seededUnread = getUnreadCount();
      if (!isChatOpened()) {
        hideChat();
        setUnreadCount(seededUnread);
      } else {
        setUnreadCount(0);
      }
    });
    const unsubReceived = onCrispEvent('message:received', (payload: unknown) => {
      const message = payload as CrispReceivedMessage | undefined;
      if (message?.origin === 'update' || isChatOpened()) return;
      setUnreadCount((prev) => Math.max(prev + 1, getUnreadCount()));
    });
    const unsubOpened = onCrispEvent('chat:opened', () => {
      setUnreadCount(0);
    });
    const unsubClosed = onCrispEvent('chat:closed', () => {
      hideChat();
    });

    return () => {
      cancelled = true;
      unsubLoaded();
      unsubReceived();
      unsubOpened();
      unsubClosed();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.isLoggedIn]);

  useEffect(() => {
    if (!settings.isLoggedIn) return;
    setCrispLocale(i18n.language);
  }, [i18n.language, settings.isLoggedIn]);

  useEffect(() => {
    if (!settings.isLoggedIn) {
      if (wasLoggedIn.current) {
        resetSession();
        hideChat();
      }
      wasLoggedIn.current = false;
      return;
    }

    wasLoggedIn.current = true;
    if (crispIdentity === undefined) return;
    let cancelled = false;

    const identify = async () => {
      let email: string | undefined;
      try {
        const user = await Auth.currentAuthenticatedUser();
        const candidate = user?.attributes?.email as string | undefined;
        if (candidate && isValidEmail(candidate)) {
          email = candidate.trim();
        }
      } catch (error) {
        console.error('Failed to read Cognito user for Crisp identity', { error });
      }

      if (cancelled) return;

      try {
        identifyVisitor({
          email,
          emailSignature: crispIdentity?.emailSignature,
          tenantId: settings.tenantId,
          storeName: settings.tenantName,
          storePlan: settings.pricingPlan,
          storeEmail: settings.storeEmail && isValidEmail(settings.storeEmail)
            ? settings.storeEmail.trim()
            : undefined,
        });
      } catch (error) {
        console.error('Failed to identify Crisp visitor', {
          tenantId: settings.tenantId,
          error,
        });
      }
    };

    identify();
    return () => {
      cancelled = true;
    };
  }, [
    settings.isLoggedIn,
    settings.tenantId,
    settings.tenantName,
    settings.pricingPlan,
    settings.storeEmail,
    crispIdentity,
  ]);

  function linkSession() {
    const sessionId = getSessionId();
    if (!sessionId) return;
    const linked = window.CRISP_TOKEN_ID ? `${window.CRISP_TOKEN_ID}:${sessionId}` : sessionId;
    if (readLinkedSession() === linked) return;

    CrispAPI.linkSession({ sessionId, ...linkContext.current })
      .then(() => writeLinkedSession(linked))
      .catch((error) => {
        console.error('Failed to link Crisp session', { sessionId, error });
      });
  }

  const openChat = useCallback(() => {
    if (!settings.isLoggedIn) return;
    openCrispChat();
  }, [settings.isLoggedIn]);

  const value = useMemo(
    () => ({ openChat, unreadCount, isReady }),
    [openChat, unreadCount, isReady]
  );

  return (
    <CrispChatContext.Provider value={value}>{children}</CrispChatContext.Provider>
  );
}

export function useCrispChat(): CrispChatContextValue {
  const context = useContext(CrispChatContext);
  if (!context) {
    throw new Error('useCrispChat must be used within CrispProvider');
  }
  return context;
}
